import { Action, createFeatureSelector, createReducer, createSelector, on, } from '@ngrx/store';
import * as Immutable from 'immutable';
import {
  loadAllShowcases,
  loadAllShowcasesComplete,
  loadMoreSingleShowcase,
  loadMoreSingleShowcaseComplete,
  loadSingleShowcase,
  loadSingleShowcaseComplete,
} from '../actions/showcases.actions';
import { ShowcasesTypes, StoredShowcaseData } from '../models/showcases';
import { FullCardState } from './full-card.reducer';

export interface StoredShowcaseState extends Omit<StoredShowcaseData, 'data' | 'type'> {
  data: Immutable.List<any>;
  pageNum: number;
}

export interface ShowcasesState {
  error: string;
  loading: boolean;
  isMouseClick: boolean;
  [ShowcasesTypes.RESOURCES]: StoredShowcaseState;
  [ShowcasesTypes.CUSTOM]: StoredShowcaseState;
  [ShowcasesTypes.SHELF]: StoredShowcaseState;
  [ShowcasesTypes.CONTRIBUTORS]: StoredShowcaseState;
  [ShowcasesTypes.TOPICS]: StoredShowcaseState;
  [ShowcasesTypes.GENRE]: StoredShowcaseState;
  [ShowcasesTypes.NYT]: StoredShowcaseState;
}

// @ts-ignore
export const showcasesFeatureInitialState: ShowcasesState = {
  error: '',
  loading: false,
  isMouseClick: false,
  ...createShowcasesInitialState(),
};

const reducer = createReducer(
  showcasesFeatureInitialState,
  on(loadAllShowcases, (state) => ({
    ...state,
    error: '',
    loading: true,
    ...resetShowcasesState(state),
  })),
  on(loadAllShowcasesComplete, (state, payload) => ({
    ...state,
    ...setShowcasesValues(state, payload),
    error: '',
    loading: false,
    isMouseClick: false,
  })),
  on(loadSingleShowcase, (state) => ({
    ...state,
    error: '',
    loading: true,
    ...resetShowcasesState(state),
  })),
  on(loadSingleShowcaseComplete, (state, {showcase, data, identifier, isMultiple}) => ({
    ...state,
    error: '',
    loading: false,
    isMouseClick: false,
    [showcase]: {
      ...state[showcase],
      identifier,
      data: Immutable.List(isMultiple ? data.hits : data.data),
      totalCount: data.totalResults,
      loading: false,
      pageNum: 1,
      name: data.name,
      metadata: data.metadata,
      isMultiple,
      showcaseId: data.showcaseId,
    },
  })),
  on(loadMoreSingleShowcase, (state, {showcase, isMouseClick}) => ({
    ...state,
    isMouseClick: !!isMouseClick,
    [showcase]: {
      ...state[showcase],
      loading: true,
      pageNum: state[showcase].pageNum + 1,
    },
  })),
  on(loadMoreSingleShowcaseComplete, (state, {showcase, data, isMultiple}) => ({
    ...state,
    [showcase]: {
      ...state[showcase],
      data: state[showcase].data.concat(isMultiple ? data.hits : data.data),
      totalCount: data.totalResults,
      loading: false,
    },
  })),
);

export function showcasesReducer(state: ShowcasesState | undefined, action: Action) {
  return reducer(state, action);
}

export const selectFullCard = createFeatureSelector<FullCardState>(
  'fullCard',
);

export const selectShowcases = createSelector(
  selectFullCard,
  (state: FullCardState): ShowcasesState => state?.showcases,
);

export const selectLoading = createSelector(
  selectShowcases,
  (state: ShowcasesState): boolean => state.loading,
);

export const selectIsMouseClick = createSelector(
  selectShowcases,
  (state: ShowcasesState): boolean => state.isMouseClick,
);

export const selectContributorsLoading = createSelector(
  selectShowcases,
  (state: ShowcasesState): boolean => state[ShowcasesTypes.CONTRIBUTORS].loading,
);

export const selectTabPageNum = createSelector(
  selectShowcases,
  (state: ShowcasesState) => state && {
    resources: state.resources.pageNum,
    staffLists: state.staffLists.pageNum,
    contributors: state.contributors.pageNum,
    topics: state.topics.pageNum,
    shelf: state.shelf.pageNum,
    genre: state.genre.pageNum,
    nyTimes: state.nyTimes.pageNum,
  },
);

export const selectAllShowcases = createSelector(
  selectShowcases,
  (state: ShowcasesState): StoredShowcaseData[] => {
    return state && Object.values(ShowcasesTypes).map((item: ShowcasesTypes) => {
      return {
        data: state[item].data.toArray(),
        totalCount: state[item].totalCount,
        loading: state[item].loading,
        identifier: state[item].identifier,
        showcaseId: state[item].showcaseId,
        name: state[item].name,
        type: item,
        metadata: state[item].metadata,
        isMultiple: state[item].isMultiple,
      };
    }).filter((showcase) => showcase.data.length > 0);
  },
);

export const selectSingleShowcase = createSelector(selectShowcases,
  (state: ShowcasesState, props: { showcase: ShowcasesTypes }): StoredShowcaseData => {
    return {
      data: state[props.showcase].data.toArray(),
      totalCount: state[props.showcase].totalCount,
      loading: state[props.showcase].loading,
      identifier: state[props.showcase].identifier,
      showcaseId: state[props.showcase].showcaseId,
      name: state[props.showcase].name,
      type: props.showcase,
      metadata: state[props.showcase].metadata,
    };
  },
);

function createShowcasesInitialState() {
  const showcasesTypes = Object.values(ShowcasesTypes);
  const singleShowcase = {
    pageNum: 0,
    totalCount: 0,
    loading: false,
    data: Immutable.List([]),
    identifier: '',
    showcaseId: '',
    name: '',
  };
  const topics = {
    pageNum: 0,
    totalCount: 0,
    loading: false,
    data: Immutable.List([]),
    name: '',
    showcaseId: '',
  };
  return showcasesTypes.reduce((prev, showcaseType: ShowcasesTypes) => {
    const isTopic = showcaseType === ShowcasesTypes.TOPICS;
    return {
      ...prev,
      [showcaseType]: isTopic ? {...topics} : {...singleShowcase},
    };
  }, {});
}

function resetShowcasesState(state: ShowcasesState) {
  return Object.values(ShowcasesTypes).reduce((prev, showcaseType: ShowcasesTypes) => {
    const isTopic = showcaseType === ShowcasesTypes.TOPICS;
    const resetShowcase = {
      ...state[showcaseType],
      pageNum: 0,
      totalCount: 0,
      data: Immutable.List([]),
      loading: true,
      showcaseId: '',
      name: '',
    };

    return isTopic ?
      {
        ...prev,
        [showcaseType]: resetShowcase,
      } :
      {
        ...prev,
        [showcaseType]: {
          ...resetShowcase,
          identifier: '',
        },
      };
  }, {});
}

function setShowcasesValues(state: ShowcasesState, payload: any) {
  return Object.values(ShowcasesTypes).reduce((prev, showcaseType: ShowcasesTypes) => {
    let updatedShowcaseState: any;
    const showcase = payload[showcaseType];
    const isMultiple = showcaseType === ShowcasesTypes.TOPICS || (showcaseType === ShowcasesTypes.CUSTOM && showcase?.hits.length > 1);
    const showcases: any = isMultiple ? extractMultipleShowcases(showcase) : extractShowcase(showcase);
    updatedShowcaseState = {
      ...state[showcaseType],
      data: Immutable.List([...showcases.data]),
      totalCount: showcases.totalCount,
      loading: false,
      identifier: showcases.identifier,
      name: showcases.name,
      showcaseId: showcases.showcaseId,
      metadata: showcases.metadata,
      isMultiple,
    };

    return {
      ...prev,
      [showcaseType]: updatedShowcaseState,
    };
  }, {});
}

function extractMultipleShowcases(showcase: any) {
  return {
    totalCount: showcase ? showcase.totalResults : 0,
    data: showcase ? showcase.hits : [],
  };
}

function extractShowcase(showcase: any) {
  const isShowcaseExisted = !!(showcase && showcase.hits.length);
  const showcaseData = isShowcaseExisted ? showcase.hits[0] : null;
  const name = isShowcaseExisted ? showcase.name : null;
  const showcaseId = isShowcaseExisted ? showcase.showcaseId : null;
  const identifier = showcaseData ? showcaseData.identifier : '';
  const relatedResources = showcaseData ? showcaseData.relatedResources : null;
  const data = relatedResources ? relatedResources.data : [];
  const totalCount = relatedResources ? relatedResources.totalResults : 0;
  const metadata = isShowcaseExisted ? showcase.hits[0].metadata : null;
  return {
    identifier,
    data,
    totalCount,
    name,
    showcaseId,
    metadata,
  };
}
